/**
 * Plain-language description of a loan type: what it costs and how repayment works.
 * Every sentence mirrors the backend's LoanService, so it can be trusted; nothing here is invented.
 *  - rate is a percent per repayment period (the period is the unit of the duration)
 *  - STRAIGHT_LINE: equal principal each period, plus fixed interest on the ORIGINAL amount
 *  - REDUCING_BALANCING: the same instalment each period, interest on the balance still owed
 *  - EQUAL_PRINCIPAL: equal principal each period, plus interest on the balance still owed (payments fall)
 *  - informal: no fixed schedule; each month rate% of the principal still owed is added as interest,
 *    and payments clear interest first, then principal
 */
import { INTEREST_APPLICATION_DAY, INTEREST_APPLICATION_WEEK } from "../../constants/loanModel";
import { formatNaira } from "../../utils/dashboardFormat";

const PERIODS = {
  DAYS: { unit: "day", plural: "days", every: "daily" },
  WEEKS: { unit: "week", plural: "weeks", every: "weekly" },
  MONTHS: { unit: "month", plural: "months", every: "monthly" },
  YEARS: { unit: "year", plural: "years", every: "yearly" },
};

export const periodFor = (durationType) => PERIODS[String(durationType || "").toUpperCase()] || null;

const hasValue = (v) => v !== null && v !== undefined && v !== "";
const toNumber = (v) => (hasValue(v) && Number.isFinite(Number(v)) ? Number(v) : null);
const trimNumber = (n) => String(Number(n.toFixed(2)));

const WEEK_WORD = {
  [INTEREST_APPLICATION_WEEK.FIRST]: "first",
  [INTEREST_APPLICATION_WEEK.SECOND]: "second",
  [INTEREST_APPLICATION_WEEK.THIRD]: "third",
  [INTEREST_APPLICATION_WEEK.FOURTH]: "fourth",
  [INTEREST_APPLICATION_WEEK.LAST]: "last",
};
const DAY_WORD = Object.values(INTEREST_APPLICATION_DAY).reduce((acc, d) => ({ ...acc, [d]: d.charAt(0).toUpperCase() + d.slice(1) }), {});

/** "the first Friday of the month", or "" when the loan type has no meeting day set. */
export function meetingDayPhrase(loanType) {
  const week = WEEK_WORD[String(loanType?.interest_application_week || "").toLowerCase()];
  const day = DAY_WORD[String(loanType?.interest_application_day || "").toLowerCase()];
  if (week && day) return `the ${week} ${day} of the month`;
  if (day) return `a ${day}`;
  return "";
}

/** "None required", "1 guarantor", "Up to 3 guarantors", "1 to 3 guarantors". */
export function guarantorText(min, max) {
  const lo = min ?? 0;
  const hi = max ?? lo;
  const noun = (n) => (n === 1 ? "guarantor" : "guarantors");
  if (hi === 0) return "None required";
  if (lo === hi) return `${hi} ${noun(hi)}`;
  if (lo === 0) return `Up to ${hi} ${noun(hi)}`;
  return `${lo} to ${hi} ${noun(hi)}`;
}

export function methodLabel(mode) {
  const m = String(mode || "").toUpperCase();
  if (m === "STRAIGHT_LINE") return "Straight line";
  if (m === "REDUCING_BALANCING" || m === "REDUCING_BALANCE") return "Reducing balance";
  if (m === "EQUAL_PRINCIPAL") return "Equal principal";
  return "";
}

/**
 * @param loanType a loan type row from the API
 * @param options.durationType the unit chosen on the form (DAYS, WEEKS, MONTHS, YEARS), if any
 * @param options.audience "calculator" hides what only matters when applying (guarantors, limits note)
 */
export function describeLoanType(loanType, { durationType, audience = "default" } = {}) {
  if (!loanType) return null;

  const informal = String(loanType.loan_model || "").toLowerCase() === "informal";
  const mode = String(loanType.interest_calculation_mode || "").toUpperCase();
  const period = periodFor(durationType);
  const rate = toNumber(loanType.rate);
  const rateText = rate === null ? "" : `${trimNumber(rate)}%`;
  const min = toNumber(loanType.min_loan_amount);
  const max = toNumber(loanType.max_loan_amount);
  const minG = toNumber(loanType.min_guarantor);
  const maxG = toNumber(loanType.max_guarantor);
  const multiplier = toNumber(loanType.borrowing_multiplier_override);
  const meeting = meetingDayPhrase(loanType);

  const facts = [];

  if (rate !== null) {
    facts.push({
      key: "rate",
      label: "Interest rate",
      value: informal ? `${rateText} a month` : period ? `${rateText} per ${period.unit}` : `${rateText} per repayment period`,
      hint: informal ? "On the principal still owed" : "",
    });
  }

  const method = informal ? "Monthly on balance" : methodLabel(mode);
  if (method) facts.push({ key: "method", label: "Interest method", value: method, hint: "" });

  if (min !== null || max !== null) {
    facts.push({
      key: "range",
      label: "Amount you can borrow",
      value: min !== null && max !== null ? `${formatNaira(min)} to ${formatNaira(max)}` : min !== null ? `From ${formatNaira(min)}` : `Up to ${formatNaira(max)}`,
      hint: "",
    });
  }

  if (audience === "default") {
    if (minG !== null || maxG !== null) {
      facts.push({ key: "guarantors", label: "Guarantors", value: guarantorText(minG, maxG), hint: "" });
    }
    if (multiplier !== null && multiplier > 0) {
      facts.push({ key: "limit", label: "Borrowing limit", value: `${trimNumber(multiplier)}x savings and shares`, hint: "Set for this loan type" });
    }
  }

  const steps = [];
  const unit = period ? period.unit : "period";
  const repaidEvery = period
    ? `Repaid ${period.every}, because the duration is in ${period.plural}.`
    : "Repayments follow the unit of the duration you choose: months means monthly, weeks means weekly.";

  if (informal) {
    if (rate !== null) {
      steps.push(`There is no fixed schedule. Once a month${meeting ? `, on ${meeting}` : ""}, ${rateText} of the principal still owed is added as interest.`);
    } else {
      steps.push("There is no fixed schedule. Interest is added once a month on the principal still owed.");
    }
    steps.push("Payments are recorded as they are made. Each one clears the interest added so far first, then reduces the principal.");
  } else if (mode === "STRAIGHT_LINE") {
    steps.push(repaidEvery);
    steps.push(`Each ${unit} you repay an equal share of the loan${rate !== null ? `, plus ${rateText} of the original amount as interest` : " plus interest on the original amount"}.`);
    steps.push(`The interest is the same every ${unit}. Total interest = ${rate !== null ? rateText : "rate"} × amount × number of ${period ? period.plural : "periods"}.`);
  } else if (mode === "EQUAL_PRINCIPAL") {
    steps.push(repaidEvery);
    steps.push(`Each ${unit} you repay an equal share of the loan${rate !== null ? `, plus ${rateText} of the balance you still owe` : " plus interest on the balance you still owe"}.`);
    steps.push(`The balance shrinks, so the interest falls and every payment is a little smaller than the one before. Total interest is lower than straight line at the same rate.`);
  } else if (mode === "REDUCING_BALANCING" || mode === "REDUCING_BALANCE") {
    steps.push(repaidEvery);
    steps.push(`You pay the same instalment every ${unit}.`);
    steps.push(`Interest is ${rate !== null ? rateText : "the rate"} of the balance you still owe, so early instalments are mostly interest and later ones mostly repay the loan. Total interest is usually lower than straight line at the same rate.`);
  }

  return {
    name: loanType.name || loanType.label || "",
    modelLabel: informal ? "Informal" : "Formal",
    facts,
    steps,
    rangeNote: audience === "default" && (min !== null || max !== null) ? "Applications outside this amount range are rejected automatically." : "",
  };
}

/**
 * Options for the "Interest Calculation Mode" dropdown. The server's list decides which methods exist
 * (so a new method shows up without a portal release); the portal only tidies their names.
 * `serverOptions` are {label, value} as stored at login; `fallback` is used when that list is missing.
 */
export function interestModeOptions(serverOptions, fallback = []) {
  const list = Array.isArray(serverOptions) && serverOptions.length > 0 ? serverOptions : fallback;
  return list.map((option) => ({ ...option, label: methodLabel(option.value) || option.label }));
}
